import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dashboardAPI, perfumeAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Package,
  TrendingUp,
  ShoppingCart,
  AlertTriangle,
  ArrowRight,
  MessageCircle,
  Globe,
  Music,
  Calculator,
  DollarSign,
} from "lucide-react";
import { toast } from "sonner";

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, lowStockRes] = await Promise.all([
        dashboardAPI.getStats(),
        perfumeAPI.getLowStock(),
      ]);
      setStats(statsRes.data);
      setLowStockItems(lowStockRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
    }).format(value || 0);
  };

  const getChannelIcon = (channel) => {
    switch (channel) {
      case "whatsapp":
        return <MessageCircle className="w-4 h-4" />;
      case "website":
        return <Globe className="w-4 h-4" />;
      case "tiktok_shop":
        return <Music className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getChannelColor = (channel) => {
    switch (channel) {
      case "whatsapp":
        return "bg-emerald-100 text-emerald-700 border-emerald-300";
      case "website":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "tiktok_shop":
        return "bg-pink-100 text-pink-700 border-pink-300";
      default:
        return "";
    }
  };

  const getChannelLabel = (channel) => {
    switch (channel) {
      case "whatsapp":
        return "WhatsApp";
      case "website":
        return "Sito Web";
      case "tiktok_shop":
        return "TikTok Shop";
      default:
        return channel;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-[#C9A227] animate-pulse font-medium">Caricamento...</div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-fadeIn">
      {/* TOP INTERACTIVE HEADER - Fatturato Totale Centrale */}
      <div className="bg-gradient-to-r from-[#1a1a2e] via-[#16213e] to-[#1a1a2e] rounded-2xl p-6 shadow-xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
          {/* Daily */}
          <div className="text-center p-4 bg-white/10 rounded-xl backdrop-blur">
            <p className="text-xs text-white/70 uppercase tracking-wider mb-1">Oggi</p>
            <p className="text-2xl font-bold text-white">{formatCurrency(stats?.today_revenue)}</p>
            <p className="text-xs text-white/50">{stats?.today_sales_count || 0} ordini</p>
          </div>
          
          {/* TOTAL - Central Big Card */}
          <div className="md:col-span-2 text-center p-6 bg-gradient-to-br from-[#C9A227] to-[#d4a944] rounded-xl shadow-lg transform md:scale-110">
            <p className="text-xs text-white/90 uppercase tracking-wider mb-2">
              Fatturato Totale (da Marzo)
            </p>
            <p className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
              {formatCurrency(stats?.total_revenue)}
            </p>
            <p className="text-sm text-white/80 mt-2">
              Si aggiorna in tempo reale
            </p>
          </div>
          
          {/* Weekly & Monthly */}
          <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
            <div className="text-center p-3 bg-white/10 rounded-xl backdrop-blur">
              <p className="text-xs text-white/70 uppercase tracking-wider">Settimana</p>
              <p className="text-xl font-bold text-white">{formatCurrency(stats?.weekly_revenue)}</p>
            </div>
            <div className="text-center p-3 bg-white/10 rounded-xl backdrop-blur">
              <p className="text-xs text-white/70 uppercase tracking-wider">Mese</p>
              <p className="text-xl font-bold text-white">{formatCurrency(stats?.monthly_revenue)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-900">Dashboard</h1>
          <p className="text-gray-500 text-sm">Panoramica del tuo business</p>
        </div>
        <Link to="/analytics-avanzate">
          <Button variant="outline" className="border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10">
            <Calculator className="w-4 h-4 mr-2" />
            Analytics Avanzate
          </Button>
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-gray-500 mb-1 font-medium">Profumi</p>
              <p className="text-2xl font-bold text-gray-900">{stats?.total_perfumes || 0}</p>
            </div>
            <div className="p-2 bg-amber-50 rounded-lg">
              <Package className="w-5 h-5 text-[#C9A227]" />
            </div>
          </div>
        </div>

        <Link to="/fatturato?period=today" className="block" data-testid="today-revenue-card">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:border-[#C9A227] hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1 font-medium">Vendite Oggi</p>
                <p className="text-2xl font-bold text-[#C9A227]">{formatCurrency(stats?.today_revenue)}</p>
                <p className="text-xs text-gray-400">{stats?.today_sales_count || 0} ordini</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg">
                <ShoppingCart className="w-5 h-5 text-[#C9A227]" />
              </div>
            </div>
          </div>
        </Link>

        <Link to="/fatturato?period=week" className="block" data-testid="weekly-revenue-card">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:border-[#C9A227] hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1 font-medium">Settimana</p>
                <p className="text-2xl font-bold text-[#C9A227]">{formatCurrency(stats?.weekly_revenue)}</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-[#C9A227]" />
              </div>
            </div>
          </div>
        </Link>

        <Link to="/fatturato?period=month" className="block" data-testid="monthly-revenue-card">
          <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:border-[#C9A227] hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-gray-500 mb-1 font-medium">Mese Corrente</p>
                <p className="text-2xl font-bold text-[#C9A227]">{formatCurrency(stats?.monthly_revenue)}</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-lg">
                <DollarSign className="w-5 h-5 text-[#C9A227]" />
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Stats Grid - Net Profit */}
      <div>
        <h2 className="text-xs font-semibold text-gray-500 mb-4 uppercase tracking-wider">
          Guadagno Netto (dopo scorporo)
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 rounded-xl shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-white/90 mb-1 font-medium">Guadagno Oggi</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(stats?.today_net_profit)}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 rounded-xl shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-white/90 mb-1 font-medium">Guadagno Settimanale</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(stats?.weekly_net_profit)}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#C9A227] to-[#D4AF37] p-6 rounded-xl shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-white/90 mb-1 font-medium">Guadagno Mensile</p>
                <p className="text-3xl font-bold text-white">
                  {formatCurrency(stats?.monthly_net_profit)}
                </p>
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <TrendingUp className="w-6 h-6 text-white" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Two columns layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Channel Breakdown */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <h3 className="text-lg font-serif text-gray-900 mb-4">
            Performance per Canale (30 giorni)
          </h3>
          <div className="space-y-4">
            {stats?.channel_breakdown && Object.entries(stats.channel_breakdown).map(([channel, data]) => (
              <div key={channel} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex items-center justify-between mb-3">
                  <Badge className={`${getChannelColor(channel)} border px-3 py-1 font-semibold`}>
                    {getChannelIcon(channel)}
                    <span className="ml-2">{getChannelLabel(channel)}</span>
                  </Badge>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1 font-medium">Fatturato</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(data.revenue)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1 font-medium">Guadagno Netto</p>
                    <p className={`text-xl font-bold ${data.profit >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                      {formatCurrency(data.profit)}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <Link to="/analytics">
              <Button 
                variant="ghost" 
                data-testid="view-analytics-btn"
                className="w-full mt-2 text-[#C9A227] hover:bg-amber-50 font-semibold"
              >
                Vedi Analytics Completi
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Low Stock Alert */}
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-serif text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Scorte Basse
            </h3>
            {lowStockItems.length > 0 && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-300 font-bold">
                {lowStockItems.length}
              </Badge>
            )}
          </div>
          <div>
            {lowStockItems.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="font-medium">Tutte le scorte sono a posto!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {lowStockItems.slice(0, 5).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-200"
                  >
                    <div>
                      <p className="text-gray-900 font-bold">{item.name}</p>
                      <p className="text-xs text-gray-500 font-medium">Cod: {item.code}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-amber-600 font-bold">
                        {item.quantity_ml.toFixed(0)} ml
                      </p>
                      <p className="text-xs text-gray-400">
                        soglia: {item.alert_threshold_ml} ml
                      </p>
                    </div>
                  </div>
                ))}

                <Link to="/inventory">
                  <Button 
                    variant="ghost" 
                    data-testid="view-inventory-btn"
                    className="w-full mt-2 text-[#C9A227] hover:bg-amber-50 font-semibold"
                  >
                    Gestisci Inventario
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-lg font-serif text-gray-900 mb-4">Azioni Rapide</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/sales">
            <Button
              data-testid="quick-new-sale-btn"
              className="w-full h-auto py-6 flex-col gap-2 bg-[#C9A227] text-white hover:bg-[#D4AF37] rounded-xl shadow-md font-semibold"
            >
              <ShoppingCart className="w-6 h-6" />
              <span>Nuova Vendita</span>
            </Button>
          </Link>
          <Link to="/inventory">
            <Button
              data-testid="quick-add-stock-btn"
              variant="outline"
              className="w-full h-auto py-6 flex-col gap-2 border-2 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl font-semibold"
            >
              <Package className="w-6 h-6" />
              <span>Gestisci Profumi</span>
            </Button>
          </Link>
          <Link to="/revenue">
            <Button
              data-testid="quick-add-revenue-btn"
              variant="outline"
              className="w-full h-auto py-6 flex-col gap-2 border-2 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl font-semibold"
            >
              <TrendingUp className="w-6 h-6" />
              <span>Registra Fatturato</span>
            </Button>
          </Link>
          <Link to="/scorporo">
            <Button
              data-testid="quick-scorporo-btn"
              variant="outline"
              className="w-full h-auto py-6 flex-col gap-2 border-2 border-gray-200 text-gray-700 hover:bg-gray-50 rounded-xl font-semibold"
            >
              <Calculator className="w-6 h-6" />
              <span>Calcola Scorporo</span>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
